import { useState } from "react";
import type { Run, ExtractorVersion } from "../types";

/* =============================================================================
   The scar ribbon — a scraper's history, time left → right, right edge = now.

   One segment per extractor version. Each new version sits on a slightly higher
   baseline, so a version boundary is a real step in the track — and a heal joins
   the two baselines with a gold diagonal: the piece was broken and rejoined. A
   human edit joins them in plain iron. The seam never fades.
   ========================================================================== */

export type Mark = "ok" | "fail" | "pause";
export type Join = "heal" | "human" | "canary";
export interface Segment {
  /** How this version was joined to the previous one. Absent on the first segment. */
  join?: Join;
  runs: Mark[];
  /** ISO timestamp per run (parallel to `runs`) — drives the hover tooltip. Optional: a mark
   *  without a timestamp still hovers (label only). */
  runAt?: string[];
}

const SLOT = 7;
const SEAM_W = 12;
const STEP = 8;
/** Beyond this the ticks stop being distinguishable — keep the recent history. */
const MAX_RUNS = 72;

function mark(status: string): Mark {
  if (status === "success") return "ok";
  if (status === "failed") return "fail";
  return "pause"; // running / pending / anything without a verdict yet
}

/** How a version was born, as a seam: gold heal (AI), plain human edit, or a waiting canary. */
function joinOf(v: ExtractorVersion | undefined): Join {
  return v?.status === "canary" ? "canary" : v?.created_by === "ai" ? "heal" : "human";
}

/**
 * Turn a scraper's runs + extractor versions into ribbon segments.
 *
 * EXACT path: runs carry `extractor_version_id`, so a boundary is a real change
 * of version between consecutive runs — the seam lands on the exact run where the
 * new extractor took over, and its type (heal / human / canary) is that version's
 * own. A run missing the id just extends the current segment; it never invents a
 * seam.
 *
 * Time fallback: when NO run carries a version id (a synthetic history, or data
 * old enough to predate the column), place each boundary at the version's
 * created_at — true enough for the mark, and what this did before.
 *
 * Either way, a version that has NEVER run still earns its seam at the end — that
 * is exactly the canary case: the fix exists, nothing has run on it yet.
 */
export function toSegments(runs: Run[], versions: ExtractorVersion[]): Segment[] {
  const byTime = [...runs]
    .sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at))
    .slice(-MAX_RUNS);

  const versionById = new Map(versions.map((v) => [v.id, v]));
  const runsCarryVersion = byTime.some((r) => r.extractor_version_id);

  const segments: Segment[] = [{ runs: [], runAt: [] }];
  const seen = new Set<string>();

  if (runsCarryVersion) {
    let currentVid: string | null = null;
    for (const run of byTime) {
      const vid = run.extractor_version_id ?? null;
      // A boundary is a version CHANGE between two runs — not the first version,
      // and never on a run whose id is missing (that just continues the segment).
      if (vid && currentVid && vid !== currentVid) {
        segments.push({ join: joinOf(versionById.get(vid)), runs: [], runAt: [] });
      }
      if (vid) {
        currentVid = vid;
        seen.add(vid);
      }
      const seg = segments[segments.length - 1];
      seg.runs.push(mark(run.status));
      seg.runAt!.push(run.started_at);
    }
  } else {
    // Time fallback (unchanged behaviour): the first version isn't a seam.
    const boundaries = [...versions]
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
      .slice(1)
      .map((v) => ({ at: Date.parse(v.created_at), join: joinOf(v) }))
      .filter((b) => !Number.isNaN(b.at));
    for (const run of byTime) {
      const t = Date.parse(run.started_at);
      while (boundaries.length > 0 && t >= boundaries[0].at) {
        segments.push({ join: boundaries.shift()!.join, runs: [], runAt: [] });
      }
      const seg = segments[segments.length - 1];
      seg.runs.push(mark(run.status));
      seg.runAt!.push(run.started_at);
    }
    for (const b of boundaries) segments.push({ join: b.join, runs: [], runAt: [] });
    return segments;
  }

  // Trailing versions that never ran — the canary awaiting a decision, or a
  // just-created heal not yet exercised. Only those NEWER than everything that
  // ran (a no-run version that predates the history is data noise, not a seam we
  // can place); appended in creation order.
  const cutoff = versions
    .filter((v) => seen.has(v.id))
    .reduce((max, v) => Math.max(max, Date.parse(v.created_at)), -Infinity);
  const trailing = versions
    .filter((v) => !seen.has(v.id) && Date.parse(v.created_at) >= cutoff)
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  for (const v of trailing) segments.push({ join: joinOf(v), runs: [], runAt: [] });

  return segments;
}

/** Does this history carry a fix that is waiting on a human? */
export function hasCanary(segments: Segment[]): boolean {
  return segments.some((s) => s.join === "canary");
}

/** How many times has this scraper been repaired? The scars never fade. */
export function scarCount(segments: Segment[]): number {
  return segments.filter((s) => s.join === "heal").length;
}

function describe(segments: Segment[]): string {
  const runs = segments.reduce((n, s) => n + s.runs.length, 0);
  const failed = segments.reduce((n, s) => n + s.runs.filter((r) => r === "fail").length, 0);
  const scars = scarCount(segments);
  const parts = [`${runs} run${runs === 1 ? "" : "s"}`];
  if (scars > 0) parts.push(`${scars} repair${scars === 1 ? "" : "s"}`);
  if (failed > 0) parts.push(`${failed} failed`);
  if (hasCanary(segments)) parts.push("a fix is waiting");
  return parts.join(", ");
}

/** Compact timestamp for the hover tooltip (e.g. "Jun 14, 2:32 PM"). */
function fmtTip(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function Ribbon({ segments, height = 52 }: { segments: Segment[]; height?: number }) {
  // Custom hover tooltip (fixed-position at the cursor entry point) — instant, unlike the native SVG
  // <title>'s ~half-second delay, and it carries a timestamp + drives the mark's hover animation.
  const [tip, setTip] = useState<{ x: number; y: number; title: string; sub: string } | null>(null);
  const show = (e: { clientX: number; clientY: number }, title: string, at?: string) =>
    setTip({ x: e.clientX, y: e.clientY, title, sub: at ? fmtTip(at) : "" });
  const hide = () => setTip(null);

  const totalRuns = segments.reduce((n, s) => n + s.runs.length, 0);
  // viewBox is sized to content so every ribbon lands flush right — "now".
  const width = 2 + totalRuns * SLOT + Math.max(0, segments.length - 1) * SEAM_W;
  const els: JSX.Element[] = [];
  let x = 2;
  let k = 0;

  segments.forEach((seg, i) => {
    const base = height - 12 - i * STEP;

    if (i > 0) {
      const prev = height - 12 - (i - 1) * STEP;
      const gold = seg.join === "heal" || seg.join === "canary";
      const seamTitle =
        seg.join === "heal"
          ? "Repaired — Amendie rebuilt the extractor and proved it on live runs"
          : seg.join === "canary"
            ? "A fix is waiting for review"
            : "Edited by hand";
      // The seam takes effect at the new version's first run.
      const seamAt = seg.runAt?.[0];
      els.push(
        <g
          key={`seam${k++}`}
          className="rmark rmark-seam"
          onMouseEnter={(e) => show(e, seamTitle, seamAt)}
          onMouseLeave={hide}
        >
          <path
            className={`rmark-vis${seg.join === "canary" ? " seam-canary" : ""}`}
            d={`M${x} ${prev} L${x + SEAM_W} ${base}`}
            stroke={gold ? "var(--kintsugi)" : "var(--iron)"}
            strokeWidth={gold ? 2.4 : 1}
            strokeDasharray={seg.join === "canary" ? "3.5 3" : undefined}
            strokeOpacity={gold ? undefined : 0.45}
            fill="none"
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
          <rect x={x} y={0} width={SEAM_W} height={height} fill="transparent" pointerEvents="all" />
        </g>,
      );
      x += SEAM_W;
    }

    els.push(
      <line
        key={`base${k++}`}
        x1={x}
        y1={base}
        x2={x + seg.runs.length * SLOT}
        y2={base}
        stroke="var(--iron)"
        strokeOpacity={0.22}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />,
    );

    seg.runs.forEach((r, j) => {
      const at = seg.runAt?.[j];
      const title = r === "ok" ? "Ran clean" : r === "fail" ? "Extraction broke" : "Run pending";
      const mx = x;
      const vis =
        r === "ok" ? (
          // quiet: most runs are clean and you shouldn't have to look at them
          <line
            className="rmark-vis"
            x1={mx} y1={base} x2={mx} y2={base - 11}
            stroke="var(--iron)" strokeOpacity={0.55} strokeWidth={1.4}
            vectorEffect="non-scaling-stroke" pointerEvents="none"
          />
        ) : r === "fail" ? (
          <line
            className="rmark-vis"
            x1={mx} y1={base + 4} x2={mx} y2={base - 20}
            stroke="var(--crack)" strokeWidth={1.8}
            vectorEffect="non-scaling-stroke" pointerEvents="none"
          />
        ) : (
          <circle
            className="rmark-vis"
            cx={mx} cy={base} r={1} fill="var(--iron)" fillOpacity={0.4} pointerEvents="none"
          />
        );
      els.push(
        <g
          key={`r${k++}`}
          className={`rmark rmark-${r}`}
          onMouseEnter={(e) => show(e, title, at)}
          onMouseLeave={hide}
        >
          {vis}
          <rect x={mx - SLOT / 2} y={0} width={SLOT} height={height} fill="transparent" pointerEvents="all" />
        </g>,
      );
      x += SLOT;
    });
  });

  return (
    <>
      <svg
        className="ribbon"
        viewBox={`0 0 ${Math.max(width, 1)} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={describe(segments)}
      >
        {els}
      </svg>
      {tip && (
        <div className="ribbon-tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
          <span className="ribbon-tip-title">{tip.title}</span>
          {tip.sub && <span className="ribbon-tip-sub">{tip.sub}</span>}
        </div>
      )}
    </>
  );
}

/** `seam` is our word for the mark, not a customer's — they read it as a repair. */
export function RibbonLegend({ operator }: { operator: boolean }) {
  return (
    <div className="legend">
      <span>
        <svg width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
          <line x1="1" y1="10" x2="21" y2="10" stroke="var(--iron)" strokeOpacity="0.18" />
          <line x1="5" y1="10" x2="5" y2="3" stroke="var(--iron)" strokeOpacity="0.5" strokeWidth="1.4" />
          <line x1="11" y1="10" x2="11" y2="3" stroke="var(--iron)" strokeOpacity="0.5" strokeWidth="1.4" />
          <line x1="17" y1="10" x2="17" y2="3" stroke="var(--iron)" strokeOpacity="0.5" strokeWidth="1.4" />
        </svg>{" "}
        ran clean
      </span>
      <span>
        <svg width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
          <line x1="1" y1="10" x2="21" y2="10" stroke="var(--iron)" strokeOpacity="0.18" />
          <line x1="8" y1="11" x2="8" y2="1" stroke="var(--crack)" strokeWidth="1.6" />
          <line x1="14" y1="11" x2="14" y2="1" stroke="var(--crack)" strokeWidth="1.6" />
        </svg>{" "}
        failed
      </span>
      <span>
        <svg width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
          <line x1="1" y1="10" x2="7" y2="10" stroke="var(--iron)" strokeOpacity="0.18" />
          <path d="M7 10 L15 3" stroke="var(--kintsugi)" strokeWidth="1.6" fill="none" />
          <line x1="15" y1="3" x2="21" y2="3" stroke="var(--iron)" strokeOpacity="0.18" />
        </svg>{" "}
        {operator ? "healed — the seam stays" : "repaired — the mark stays"}
      </span>
      <span>
        <svg width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
          <line x1="1" y1="10" x2="7" y2="10" stroke="var(--iron)" strokeOpacity="0.18" />
          <path
            d="M7 10 L15 3" stroke="var(--kintsugi)" strokeWidth="1.6" strokeDasharray="3 2.5"
            fill="none" className="seam-canary"
          />
          <line x1="15" y1="3" x2="21" y2="3" stroke="var(--iron)" strokeOpacity="0.18" />
        </svg>{" "}
        fix waiting on you
      </span>
    </div>
  );
}
